/* ============================================================
   Petite Studio custom reviews — product page main widget.

   Renders into [data-ps-reviews-widget]:
     1. Summary header: average, stars, review count, histogram
        (histogram always shows, regardless of average), plus a
        Customer Consensus fit line when a clear majority exists.
     2. Media gallery: horizontal strip of ALL images/videos from
        all pooled reviews, in API order. Tap = lightbox at item.
     3. Review list in exact API order (never re-sorted), first 10
        shown; each "Load More Reviews" click appends 25. Verified / Influencer /
        Featured badges, customer-fit block (v1.2), media
        thumbnails, store replies.
     4. Lightbox with prev/next across all media, inline video
        playback.

   Contract rules honored here:
   - Default sort only. No sort param. No client-side re-sorting.
   - per_page=50, hard cap 10 pages (500 reviews).
   - Fail silent: ANY fetch failure = render nothing at all.
   - Zero reviews = render nothing at all.
   - No credentials, no custom headers, plain GET fetch.
   - All API text inserted via textContent (never innerHTML).
   - Media URLs always read from the API response, never cached
     in theme files.

   Exposes window.PSReviews = { productId, summary, reviews }
   (promises) so the top widget (Phase 4) reuses the same fetches.
   ============================================================ */

(function () {
  'use strict';

  var API_BASE = 'https://petite-studio-app.vercel.app/api/public';
  var PER_PAGE = 50;
  var MAX_PAGES = 10;

  /* ---------- data layer ---------- */

  function fetchJson(url) {
    return fetch(url).then(function (res) {
      if (!res.ok) throw new Error('http ' + res.status);
      return res.json();
    });
  }

  function fetchSummary(productId) {
    return fetchJson(
      API_BASE + '/reviews/summary?product_id=' + productId
    ).catch(function () {
      return null;
    });
  }

  function fetchAllReviews(productId) {
    var base =
      API_BASE + '/reviews?product_id=' + productId + '&per_page=' + PER_PAGE;
    return fetchJson(base + '&page=1')
      .then(function (first) {
        var reviews = first.reviews || [];
        var pages = Math.min(first.total_pages || 0, MAX_PAGES);
        if (pages <= 1) return { total: first.total || 0, reviews: reviews };

        var requests = [];
        for (var p = 2; p <= pages; p++) {
          requests.push(fetchJson(base + '&page=' + p));
        }
        /* Any page failing rejects the whole thing -> render nothing */
        return Promise.all(requests).then(function (rest) {
          var all = reviews.slice();
          for (var i = 0; i < rest.length; i++) {
            all = all.concat(rest[i].reviews || []);
          }
          return { total: first.total || 0, reviews: all };
        });
      })
      .catch(function () {
        return null;
      });
  }

  /* ---------- tiny DOM helpers (API text only via textContent) ---------- */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function starsNode(rating, className) {
    var wrap = el('span', 'ps-stars ' + (className || ''));
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', rating + ' out of 5 stars');
    var bg = el('span', 'ps-stars__bg', '★★★★★');
    bg.setAttribute('aria-hidden', 'true');
    var fg = el('span', 'ps-stars__fg', '★★★★★');
    fg.setAttribute('aria-hidden', 'true');
    var pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    fg.style.width = pct + '%';
    wrap.appendChild(bg);
    wrap.appendChild(fg);
    return wrap;
  }

  function formatDate(iso) {
    try {
      var d = new Date(iso);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return '';
    }
  }

  /* ---------- customer-fit fields (contract v1.2) ---------- */

  /* Display-only label remap for fit_feedback. Raw API vocabulary is
     the key; anything unrecognized falls through to raw display. */
  var FIT_LABELS = {
    'True to Size': 'True to size',
    'Larger than Expected': 'Runs large',
    'Smaller than Expected': 'Runs small'
  };

  /* Multi-select values arrive comma-joined with inconsistent
     spacing — split, trim, map each, re-join. */
  function fitFeedbackLabel(raw) {
    return raw
      .split(',')
      .map(function (part) { return part.trim(); })
      .filter(function (part) { return part.length > 0; })
      .map(function (part) { return FIT_LABELS[part] || part; })
      .join(', ');
  }

  /* Labeled Height / Weight / Size / Fit block. Values render AS-IS
     (raw text as typed — the API never normalizes and neither do we).
     Returns null when all four fields are absent — the majority case. */
  function fitBlock(review) {
    var rows = [];
    if (review.reviewer_height) rows.push(['Height', review.reviewer_height]);
    if (review.reviewer_weight) rows.push(['Weight', review.reviewer_weight]);
    if (review.reviewer_size) rows.push(['Size', review.reviewer_size]);
    if (review.fit_feedback) rows.push(['Fit', fitFeedbackLabel(review.fit_feedback)]);
    if (!rows.length) return null;

    var block = el('div', 'ps-review__fit');
    rows.forEach(function (row) {
      var item = el('span', 'ps-review__fit-item');
      item.appendChild(el('span', 'ps-review__fit-label', row[0] + ':'));
      item.appendChild(el('span', 'ps-review__fit-value', row[1]));
      block.appendChild(item);
    });
    return block;
  }

  /* Consensus across all pooled reviews with fit_feedback: every
     comma-separated selection counts as one vote. Returns the display
     line, or null when nothing is populated, when there is ANY tie
     for most frequent, or when an unrecognized raw value wins. */
  var CONSENSUS_LABELS = {
    'True to Size': 'Fits True to Size',
    'Larger than Expected': 'Runs Large / Size Down',
    'Smaller than Expected': 'Runs Small / Size Up'
  };

  function consensusLine(reviews) {
    var tally = {};
    for (var i = 0; i < reviews.length; i++) {
      var raw = reviews[i] && reviews[i].fit_feedback;
      if (!raw) continue;
      var parts = raw.split(',');
      for (var p = 0; p < parts.length; p++) {
        var value = parts[p].trim();
        if (!value) continue;
        tally[value] = (tally[value] || 0) + 1;
      }
    }

    var winner = null;
    var best = 0;
    var tied = false;
    for (var key in tally) {
      if (!Object.prototype.hasOwnProperty.call(tally, key)) continue;
      if (tally[key] > best) {
        best = tally[key];
        winner = key;
        tied = false;
      } else if (tally[key] === best) {
        tied = true;
      }
    }

    if (!winner || tied) return null;
    var label = CONSENSUS_LABELS[winner];
    if (!label) return null; /* unrecognized value won — no invented label */
    return 'Customer Fit Consensus: ' + label;
  }

  function truncate(text, max) {
    if (!text || text.length <= max) return text || '';
    var cut = text.slice(0, max + 1);
    var space = cut.lastIndexOf(' ');
    if (space > 40) cut = cut.slice(0, space);
    else cut = text.slice(0, max);
    return cut.replace(/[\s.,;:!?]+$/, '') + '…';
  }

  /* ---------- lazy media loading ---------- */

  var mediaObserver = null;
  if ('IntersectionObserver' in window) {
    mediaObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          mediaObserver.unobserve(entry.target);
          var node = entry.target;
          var src = node.getAttribute('data-ps-src');
          if (src) {
            node.setAttribute('src', src);
            node.removeAttribute('data-ps-src');
          }
        });
      },
      { rootMargin: '300px 0px 300px 300px' }
    );
  }

  function lazyMedia(node) {
    if (mediaObserver) {
      mediaObserver.observe(node);
    } else {
      var src = node.getAttribute('data-ps-src');
      if (src) node.setAttribute('src', src);
    }
  }

  /* Creates an <img> or muted <video> thumbnail for a media item */
  function mediaThumb(media, className) {
    var node;
    if (media.media_type === 'video') {
      node = el('span', className + ' ps-media--video');
      var video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.setAttribute('data-ps-src', media.url);
      video.setAttribute('tabindex', '-1');
      lazyMedia(video);
      node.appendChild(video);
      node.appendChild(el('span', 'ps-media__play', '▶'));
    } else {
      node = el('span', className);
      var img = document.createElement('img');
      img.alt = 'Customer review photo';
      img.loading = 'lazy';
      img.setAttribute('data-ps-src', media.url);
      lazyMedia(img);
      node.appendChild(img);
    }
    return node;
  }

  /* ---------- lightbox ---------- */

  var lightbox = {
    overlay: null,
    items: [],
    index: 0,

    open: function (items, index) {
      this.items = items;
      this.index = index;
      if (!this.overlay) this.build();
      document.body.classList.add('ps-no-scroll');
      this.overlay.style.display = 'flex';
      this.show();
    },

    build: function () {
      var self = this;
      var overlay = el('div', 'ps-lightbox');
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', 'Review media viewer');

      var closeBtn = el('button', 'ps-lightbox__close', '×');
      closeBtn.setAttribute('aria-label', 'Close');
      closeBtn.type = 'button';
      closeBtn.addEventListener('click', function (e) { e.stopPropagation(); self.close(); });

      var prevBtn = el('button', 'ps-lightbox__nav ps-lightbox__nav--prev', '‹');
      prevBtn.setAttribute('aria-label', 'Previous');
      prevBtn.type = 'button';
      prevBtn.addEventListener('click', function (e) { e.stopPropagation(); self.step(-1); });

      var nextBtn = el('button', 'ps-lightbox__nav ps-lightbox__nav--next', '›');
      nextBtn.setAttribute('aria-label', 'Next');
      nextBtn.type = 'button';
      nextBtn.addEventListener('click', function (e) { e.stopPropagation(); self.step(1); });

      /* content = media box + close button pinned to the media's
         top-right corner. Clicking anywhere outside the media
         (the overlay backdrop) closes. */
      var content = el('div', 'ps-lightbox__content');
      content.addEventListener('click', function (e) { e.stopPropagation(); });

      var mediaBox = el('div', 'ps-lightbox__media');
      content.appendChild(mediaBox);
      content.appendChild(closeBtn);

      var caption = el('div', 'ps-lightbox__caption');

      overlay.appendChild(prevBtn);
      overlay.appendChild(content);
      overlay.appendChild(nextBtn);
      overlay.appendChild(caption);
      overlay.addEventListener('click', function () { self.close(); });

      document.addEventListener('keydown', function (e) {
        if (!self.overlay || self.overlay.style.display === 'none') return;
        if (e.key === 'Escape') self.close();
        if (e.key === 'ArrowLeft') self.step(-1);
        if (e.key === 'ArrowRight') self.step(1);
      });

      document.body.appendChild(overlay);
      this.overlay = overlay;
      this.content = content;
      this.mediaBox = mediaBox;
      this.caption = caption;
    },

    show: function () {
      var item = this.items[this.index];
      if (!item) return;
      this.mediaBox.innerHTML = '';
      if (item.media.media_type === 'video') {
        var video = document.createElement('video');
        video.src = item.media.url;
        video.controls = true;
        video.autoplay = true;
        video.playsInline = true;
        this.mediaBox.appendChild(video);
      } else {
        var img = document.createElement('img');
        img.src = item.media.url;
        img.alt = 'Customer review photo';
        this.mediaBox.appendChild(img);
      }
      this.caption.textContent = item.caption || '';
      var nav = this.items.length > 1 ? '' : 'none';
      this.overlay.querySelector('.ps-lightbox__nav--prev').style.display = nav;
      this.overlay.querySelector('.ps-lightbox__nav--next').style.display = nav;
    },

    step: function (dir) {
      this.index = (this.index + dir + this.items.length) % this.items.length;
      this.show();
    },

    close: function () {
      if (!this.overlay) return;
      this.mediaBox.innerHTML = ''; /* stops any playing video */
      this.overlay.style.display = 'none';
      document.body.classList.remove('ps-no-scroll');
    }
  };

  /* ---------- rendering ---------- */

  function renderSummaryHeader(container, summary, reviews) {
    /* Consensus line sits centered above the whole summary header */
    var consensus = consensusLine(reviews || []);
    if (consensus) {
      container.appendChild(el('div', 'ps-reviews__consensus', consensus));
    }

    var header = el('div', 'ps-reviews__header');

    var left = el('div', 'ps-reviews__score');
    left.appendChild(el('span', 'ps-reviews__average', summary.average.toFixed(1)));
    left.appendChild(starsNode(summary.average, 'ps-stars--large'));
    left.appendChild(
      el(
        'span',
        'ps-reviews__count',
        summary.count === 1 ? 'Based on 1 review' : 'Based on ' + summary.count + ' reviews'
      )
    );

    var right = el('div', 'ps-reviews__histogram');
    for (var star = 5; star >= 1; star--) {
      var n = summary.distribution && summary.distribution[String(star)];
      n = typeof n === 'number' ? n : 0;
      var row = el('div', 'ps-histogram__row');
      row.appendChild(el('span', 'ps-histogram__label', star + ' ★'));
      var track = el('span', 'ps-histogram__track');
      var bar = el('span', 'ps-histogram__bar');
      bar.style.width =
        summary.count > 0 ? Math.round((n / summary.count) * 100) + '%' : '0%';
      track.appendChild(bar);
      row.appendChild(track);
      row.appendChild(el('span', 'ps-histogram__num', String(n)));
      right.appendChild(row);
    }

    header.appendChild(left);
    header.appendChild(right);
    container.appendChild(header);
  }

  function buildMediaIndex(reviews) {
    /* Flat list of all media in API order (review order, then media
       position order — API already orders media by position) */
    var items = [];
    for (var i = 0; i < reviews.length; i++) {
      var review = reviews[i];
      var media = review.media || [];
      for (var m = 0; m < media.length; m++) {
        if (!media[m] || !media[m].url) continue;
        items.push({
          media: media[m],
          caption: (review.author || 'Anonymous') +
            (review.title ? ' — ' + review.title : '')
        });
      }
    }
    return items;
  }

  function renderGallery(container, mediaItems) {
    if (!mediaItems.length) return;
    var strip = el('div', 'ps-reviews__gallery');
    strip.setAttribute('aria-label', 'Customer photos and videos');
    mediaItems.forEach(function (item, index) {
      var cell = mediaThumb(item.media, 'ps-gallery__item');
      cell.setAttribute('role', 'button');
      cell.setAttribute('tabindex', '0');
      cell.setAttribute('aria-label', 'Open review media ' + (index + 1));
      function openIt() { lightbox.open(mediaItems, index); }
      cell.addEventListener('click', openIt);
      cell.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openIt(); }
      });
      strip.appendChild(cell);
    });
    container.appendChild(strip);
  }

  function renderReviewCard(review, mediaItems) {
    var card = el('article', 'ps-review');
    if (review.source === 'influencer') card.setAttribute('data-source', 'influencer');

    var head = el('div', 'ps-review__head');
    head.appendChild(starsNode(review.rating || 0, 'ps-stars--small'));

    var badges = el('span', 'ps-review__badges');
    if (review.verified) {
      badges.appendChild(el('span', 'ps-badge ps-badge--verified', '✓ Verified Buyer'));
    }
    if (review.source === 'influencer') {
      badges.appendChild(el('span', 'ps-badge ps-badge--influencer', 'Influencer'));
    }
    if (review.featured) {
      badges.appendChild(el('span', 'ps-badge ps-badge--featured', 'Featured'));
    }
    head.appendChild(badges);
    card.appendChild(head);

    /* Customer-fit block (v1.2) — only when at least one field is
       populated; absent fields leave zero footprint */
    var fit = fitBlock(review);
    if (fit) card.appendChild(fit);

    if (review.title) card.appendChild(el('h4', 'ps-review__title', review.title));
    if (review.body) card.appendChild(el('p', 'ps-review__body', review.body));

    /* Review's own media thumbnails -> open lightbox at the right
       position within the full pooled media list */
    var media = review.media || [];
    if (media.length) {
      var row = el('div', 'ps-review__media');
      media.forEach(function (m) {
        if (!m || !m.url) return;
        var thumbIndex = -1;
        for (var i = 0; i < mediaItems.length; i++) {
          if (mediaItems[i].media === m) { thumbIndex = i; break; }
        }
        var thumb = mediaThumb(m, 'ps-review__thumb');
        if (thumbIndex >= 0) {
          thumb.setAttribute('role', 'button');
          thumb.setAttribute('tabindex', '0');
          thumb.setAttribute('aria-label', 'Open review media');
          thumb.addEventListener('click', function () {
            lightbox.open(mediaItems, thumbIndex);
          });
          thumb.addEventListener('keydown', function (e) {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              lightbox.open(mediaItems, thumbIndex);
            }
          });
        }
        row.appendChild(thumb);
      });
      card.appendChild(row);
    }

    var meta = el('div', 'ps-review__meta');
    meta.appendChild(el('span', 'ps-review__author', review.author || 'Anonymous'));
    var date = formatDate(review.created_at);
    /* FTC disclosure for incentivized reviews rides on the date line:
       "September 26, 2026; received store credit" */
    var incentivized = review.incentivized === true;
    if (date || incentivized) {
      var dateText = date || '';
      if (incentivized) dateText += (date ? '; ' : '') + 'received store credit';
      meta.appendChild(el('span', 'ps-review__date', dateText));
    }
    card.appendChild(meta);

    if (review.reply) {
      var reply = el('div', 'ps-review__reply');
      reply.appendChild(el('strong', null, 'Petite Studio'));
      reply.appendChild(el('p', null, review.reply));
      card.appendChild(reply);
    }

    return card;
  }

  function renderList(container, reviews, mediaItems, pageSize) {
    var list = el('div', 'ps-reviews__list');
    container.appendChild(list);

    var shown = 0;

    var moreBtn = el('button', 'ps-reviews__more', 'Load More Reviews');
    moreBtn.type = 'button';

    function show(count) {
      var next = reviews.slice(shown, shown + count);
      next.forEach(function (review) {
        list.appendChild(renderReviewCard(review, mediaItems));
      });
      shown += next.length;
      if (shown >= reviews.length) {
        moreBtn.style.display = 'none';
      }
    }

    /* Each click appends 25 more; the button sits below the list so
       it lands at the bottom of every batch, until all are shown */
    var LOAD_MORE_BATCH = 25;
    moreBtn.addEventListener('click', function () { show(LOAD_MORE_BATCH); });
    show(pageSize);

    if (shown < reviews.length) container.appendChild(moreBtn);
  }

  /* ---------- init ---------- */

  function init() {
    var container = document.querySelector('[data-ps-reviews-widget]');
    if (!container) return;

    var productId = container.getAttribute('data-product-id') || '';
    if (!/^\d+$/.test(productId)) return;

    var pageSize = parseInt(container.getAttribute('data-page-size'), 10);
    if (!pageSize || pageSize < 1) pageSize = 10;

    var summaryPromise = fetchSummary(productId);
    var reviewsPromise = fetchAllReviews(productId);

    /* Shared with the top widget (Phase 4) — same data, no re-fetch */
    window.PSReviews = {
      productId: productId,
      summary: summaryPromise,
      reviews: reviewsPromise
    };

    Promise.all([summaryPromise, reviewsPromise])
      .then(function (results) {
        var summary = results[0];
        var data = results[1];

        /* Fail silent: any failure or zero reviews = nothing renders */
        if (!summary || !data || !summary.count || !data.reviews.length) return;
        if (summary.average === null || summary.average === undefined) return;

        try {
          var mediaItems = buildMediaIndex(data.reviews);
          var inner = el('div', 'ps-reviews__inner');
          renderSummaryHeader(inner, summary, data.reviews);
          renderGallery(inner, mediaItems);
          renderList(inner, data.reviews, mediaItems, pageSize);
          container.appendChild(inner);
        } catch (e) {
          /* fail silent — leave container empty */
        }
      })
      .catch(function () {
        /* fail silent */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
